import styled from 'styled-components'
import { backgroundBlue, blackOrange, blue, border2, textMuted, white } from '@/theme/colors'
import { PageColumn } from '@components/Page'
import { layers, media, motion, radii } from '@/theme/tokens'
import { text } from '@/theme/typography'
import { ItineraryCard } from '../ItineraryPage.styles'

/**
 * The standard page column — Quick Edit has no map to spend a wide screen on —
 * filling the scrolling ground above the tabletWide breakpoint, as the itinerary's
 * does, so the card under the heading runs to the bottom of the viewport.
 */
export const QuickEditColumn = styled(PageColumn)`
	${media.aboveTabletWide} {
		flex: 1 0 auto;
		display: flex;
		flex-direction: column;
		padding-bottom: 24px;
	}
`

/**
 * The itinerary's card with the day and the map traded for one list: the same
 * left column, and every Trip day beside it. Above the tabletWide breakpoint the
 * card runs to the bottom of the viewport and the list scrolls inside it;
 * below that the card stacks and the page scrolls, so the card clips without
 * becoming a scroll container — which would take the sticky day headers away
 * from the page they have to stick to.
 */
export const QuickEditCard = styled(ItineraryCard)`
	overflow: clip;

	${media.aboveTabletWide} {
		overflow: hidden;
		grid-template-columns: clamp(280px, 28%, 400px) minmax(0, 1fr);
	}
`

export const ListColumn = styled.div`
	min-width: 0;
	padding: 0 24px 24px;

	${media.aboveTabletWide} {
		min-height: 0;
		overflow-y: auto;
		scrollbar-width: thin;
	}

	${media.phone} {
		padding: 0 16px 16px;
	}
`

export const DayRoot = styled.section`
	display: flex;
	flex-direction: column;
`

/** The Day header, held at the top of whatever scrolls so the day in view is always named. Rows in the air pass under it. */
export const DayHeaderBar = styled.header`
	position: sticky;
	top: 0;
	z-index: ${layers.sticky};
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 24px 0 12px;
	background: ${white};
`

export const DayHeaderText = styled.div`
	min-width: 0;
	flex: 1;
`

export const DayHeaderActions = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	flex: none;
`

export const DayTitle = styled.h2`
	${text.title}
	color: ${blackOrange};
	margin: 0;
	overflow-wrap: anywhere;
`

export const DaySubtitle = styled.div`
	${text.caption}
	color: ${textMuted};
	margin-top: 2px;
`

export const RowList = styled.ul`
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 8px;
`

/** A day with nothing on it: an open slot the height of a row, which takes the accent while a dragged event is over it. */
export const EmptySlot = styled.div<{ $over: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 52px;
	border: 1px dashed ${({ $over }) => ($over ? blue : border2)};
	border-radius: ${radii.inner};
	background: ${({ $over }) => ($over ? backgroundBlue : 'transparent')};
	${text.caption}
	color: ${textMuted};
	transition:
		border-color ${motion.fast},
		background-color ${motion.fast};
`
